// ==================== SESSION DETAIL PAGE ====================
// src/pages/SessionDetail.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/client";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

type SessionStatus = "open" | "done" | "in_progress" | "archived" | string;

type RetroSession = {
  id: string;
  sprintLabel: string;
  status: SessionStatus;
  createdAt: string;
};

type RetroItem = {
  id: string;
  text: string;
  section: string;
  tags?: string[];
};

type RetroGroup = {
  id: string;
  title: string;
  items: RetroItem[];
};

type ActionStatus = "open" | "done" | "blocked" | "dropped" | string;

type RetroAction = {
  id: string;
  text: string;
  ownerName: string;
  dueDate: string;
  status: ActionStatus;
  successCriteria?: string;
};

export default function SessionDetail() {
  const { id } = useParams();
  const [session, setSession] = useState<RetroSession | null>(null);
  const [items, setItems] = useState<RetroItem[]>([]);
  const [groups, setGroups] = useState<RetroGroup[]>([]);
  const [actions, setActions] = useState<RetroAction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSession();
  }, [id]);

  async function loadSession() {
    if (!id) return;
    const sessionId = id as string;
    try {
      const [sessionData, itemsData, groupsData, actionsData] =
        await Promise.all([
          api.getSession(sessionId),
          api.getSessionItems(sessionId),
          api.getSessionGroups(sessionId),
          api.getSessionActions(sessionId),
        ]);
      setSession(sessionData);
      setItems(itemsData);
      setGroups(groupsData);
      setActions(actionsData);
    } catch (error) {
      console.error("Failed to load session", error);
    } finally {
      setLoading(false);
    }
  }

  if (loading || !session) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8">
      <Link
        to="/sessions"
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-6"
      >
        <ArrowLeft size={20} />
        Back to Sessions
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{session.sprintLabel}</h1>
        <div className="flex items-center gap-4 text-gray-600">
          <span>
            Status: <StatusBadge status={session.status} />
          </span>
          <span>•</span>
          <span>{new Date(session.createdAt).toLocaleDateString()}</span>
          <span>•</span>
          <span>{items.length} items</span>
          <span>•</span>
          <span>{actions.length} actions</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Items by Section">
          {["went_well", "improve", "ideas", "kudos"].map((section) => {
            const sectionItems = items.filter((i) => i.section === section);
            if (sectionItems.length === 0) return null;
            return (
              <div key={section} className="mb-4">
                <h3 className="font-semibold mb-2 capitalize">
                  {section.replace("_", " ")}
                </h3>
                <ul className="space-y-2">
                  {sectionItems.map((item) => (
                    <li
                      key={item.id}
                      className="text-sm p-2 bg-gray-50 rounded"
                    >
                      {item.text}
                      {item.tags && item.tags.length > 0 && (
                        <div className="mt-1">
                          {(item.tags ?? []).map((tag: string) => (
                            <span
                              key={tag}
                              className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded mr-1"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </Card>

        <Card title="Groups">
          {groups.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No groups yet</div>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => (
                <div key={group.id} className="p-4 bg-gray-50 rounded">
                  <h3 className="font-semibold mb-2">{group.title}</h3>
                  <ul className="text-sm space-y-1">
                    {group.items.map((item) => (
                      <li key={item.id}>• {item.text}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card title="Action Items">
        {actions.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            No action items yet
          </div>
        ) : (
          <div className="space-y-3">
            {actions.map((action) => (
              <div key={action.id} className="p-4 bg-gray-50 rounded">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-medium flex-1">{action.text}</div>
                  <ActionStatusBadge status={action.status} />
                </div>
                <div className="text-sm text-gray-600">
                  Owner: {action.ownerName} • Due: {action.dueDate}
                </div>
                {action.successCriteria && (
                  <div className="text-sm text-gray-600 mt-1">
                    Success: {action.successCriteria}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ActionStatusBadge({ status }: { status: ActionStatus }) {
  const colors: Record<string, string> = {
    open: "bg-blue-100 text-blue-800",
    done: "bg-green-100 text-green-800",
    blocked: "bg-red-100 text-red-800",
    dropped: "bg-gray-100 text-gray-800",
  };

  const colorClass = colors[status] || "bg-gray-100 text-gray-800";

  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded ${colorClass}`}>
      {status}
    </span>
  );
}

function StatusBadge({ status }: { status: SessionStatus }) {
  const colors: Record<string, string> = {
    open: "bg-blue-100 text-blue-800",
    in_progress: "bg-yellow-100 text-yellow-800",
    done: "bg-green-100 text-green-800",
    archived: "bg-gray-100 text-gray-800",
  };

  const colorClass = colors[status] || "bg-gray-100 text-gray-800";

  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded ${colorClass}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}
